import type { Contract } from '@/types/ContractType';

export type PartyContact = {
  name?: string;
  email?: string;
  phone?: string;
};

export type PartyContacts = {
  people: PartyContact[];
  mailingAddress: string | null;
};

const clean = (v?: string | null) => (v ?? '').trim() || undefined;

function addressLine(a?: {
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
}): string | null {
  if (!a?.street || !a.city || !a.state || !a.zip) return null;
  return `${a.street}, ${a.city}, ${a.state} ${a.zip}`;
}

function people(list: PartyContact[]): PartyContact[] {
  return list.filter((p) => p.name || p.email || p.phone);
}

/**
 * Contact block for the contract's parties — one entry per person, used by
 * both the on-screen contract and the PDF so they print the same thing.
 * Seller details fall back to what the listing agent entered.
 */
export function contractPartyContacts(contract?: Partial<Contract> | null): {
  buyer: PartyContacts;
  seller: PartyContacts;
} {
  const b = contract?.buyerInfo;
  const s = contract?.sellerInfo;
  const a = contract?.agentSellerInfo;
  const twoSellers =
    Number(s?.numberofSellers ?? a?.numberOfSellers ?? 0) !== 1;

  return {
    buyer: {
      people: people([
        {
          name: clean(b?.name),
          email: clean(b?.email),
          phone: clean(b?.phone),
        },
        {
          name: clean(b?.name2),
          email: clean(b?.email2),
          phone: clean(b?.phone2),
        },
      ]),
      mailingAddress: addressLine(b),
    },
    seller: {
      people: people([
        {
          name: clean(s?.name) ?? clean(a?.sellerName),
          email: clean(s?.email) ?? clean(a?.sellerEmail),
          phone: clean(s?.phone) ?? clean(a?.sellerPhone),
        },
        ...(twoSellers
          ? [
              {
                name: clean(s?.name2) ?? clean(a?.seller2Name),
                email: clean(s?.email2) ?? clean(a?.seller2Email),
                phone: clean(s?.phone2) ?? clean(a?.seller2Phone),
              },
            ]
          : []),
      ]),
      mailingAddress: addressLine(s),
    },
  };
}
